// API-specific error handling utilities
import { ApiError, NetworkError, AuthenticationError, ERROR_CODE_UNKNOWN_API_ERROR } from "./index";
import { InputDecodingFailedError } from "../utils/unwrap";

// Create error from fetch response
export async function createApiError(response: Response): Promise<ApiError> {
    try {
        const data = await response.json();
        return ApiError.fromResponse(response.status, data);
    } catch (error: unknown) {
        // If response body is not JSON, create generic error
        return ApiError.fromResponse(response.status, {
            message: `HTTP ${response.status}: ${response.statusText}`,
            errorCode: ERROR_CODE_UNKNOWN_API_ERROR,
        });
    }
}

// Handle network/fetch errors
export function throwFetchError(error: unknown): never {
    if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new NetworkError("Network request failed - check your connection");
    }

    if (error instanceof Error) {
        throw new NetworkError(`Network error: ${error.message}`, { cause: error });
    }

    throw new NetworkError("Unknown network error occurred");
}

// Check if error should trigger re-authentication
export function shouldReauthenticate(error: unknown): boolean {
    if (error instanceof ApiError) {
        return (
            error.statusCode === 401 ||
            error.statusCode === 403 ||
            (error.errorCode >= 4031 && error.errorCode <= 40335)
        );
    }
    return false;
}

// Extract user-friendly message from any error
export function getUserFriendlyMessage(error: unknown): string {
    if (error instanceof ApiError) {
        // Use backend error message
        return error.message;
    }

    if (error instanceof NetworkError) {
        return "Connection problem. Please check your internet connection.";
    }

    if (error instanceof AuthenticationError) {
        return "Please log in to continue.";
    }

    if (error instanceof InputDecodingFailedError) {
        return "Received invalid data from server. Please try again.";
    }

    if (error instanceof Error) {
        return error.message;
    }

    return "An unexpected error occurred. Please try again.";
}

// Extract error code for logging/analytics
export function getErrorCode(error: unknown): number | null {
    if (error instanceof ApiError) {
        return error.errorCode;
    }
    return null;
}
